const DAY_MILLISECONDS = 86_400_000;
const DEFAULT_BRIDGE_CHOICE_COUNT = 4;
const DEFAULT_COLLECTION_LIMIT = 6;
const CROSS_ERA_YEARS = 50;

export type ChallengeClock = Date | number | string;

export type UtcDayPeriod = {
  key: `utc-day-${number}`;
  index: number;
  date: string;
};

export type ChallengeCandidate = {
  id: string;
  source: string;
  target: string;
  steps: number;
  eligible: boolean;
};

export type ChallengeFallbackReason =
  | 'duplicate-candidate'
  | 'empty-pool'
  | 'ineligible-candidate'
  | 'invalid-clock'
  | 'repeated-endpoint';

export type ChallengeSelection<Candidate extends ChallengeCandidate> =
  | {
      kind: 'selected';
      period: UtcDayPeriod;
      candidate: Candidate;
    }
  | {
      kind: 'fallback';
      period?: UtcDayPeriod;
      reason: ChallengeFallbackReason;
    };

export type ChallengeCopy = {
  mode: 'current-day' | 'snapshot-fallback';
  navigationLabel: string;
  kicker: string;
  callToAction: string;
  periodLabel: string;
  titleSuffix: string;
};

export type BridgePerson = {
  id: string;
  name: string;
  eligible: boolean;
};

export type BridgeCandidate = ChallengeCandidate & {
  people: readonly BridgePerson[];
};

export type BridgeChoice = {
  id: string;
  name: string;
};

export type BridgePrompt = {
  answerId: string;
  choices: readonly BridgeChoice[];
};

export type CollectionPerson = {
  id: string;
  birthYear?: number;
  domains: readonly string[];
};

export type CollectionCandidate = ChallengeCandidate & {
  sourcePerson: CollectionPerson;
  targetPerson: CollectionPerson;
};

export type UnexpectedConnectionCollectionId =
  'across-domains' | 'across-eras' | 'quick-surprises' | 'deeper-paths' | 'accepted-featured';

export type UnexpectedConnectionCollection = {
  id: UnexpectedConnectionCollectionId;
  title: string;
  description: string;
  traceIds: readonly string[];
};

function clockMilliseconds(clock: ChallengeClock): number {
  if (clock instanceof Date) return clock.getTime();
  return typeof clock === 'number' ? clock : Date.parse(clock);
}

function stableHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function deterministicOrder<T>(
  values: readonly T[],
  seed: string,
  identity: (value: T) => string,
): T[] {
  return [...values].sort(
    (left, right) =>
      stableHash(`${seed}:${identity(left)}`) - stableHash(`${seed}:${identity(right)}`) ||
      identity(left).localeCompare(identity(right)),
  );
}

/** Returns the absolute UTC day containing the supplied instant. */
export function utcDayPeriod(clock: ChallengeClock): UtcDayPeriod {
  const milliseconds = clockMilliseconds(clock);
  if (!Number.isFinite(milliseconds)) {
    throw new Error('Challenge clock must be a valid instant.');
  }
  const index = Math.floor(milliseconds / DAY_MILLISECONDS);
  return {
    key: `utc-day-${index}`,
    index,
    date: new Date(index * DAY_MILLISECONDS).toISOString().slice(0, 10),
  };
}

/**
 * Selects from the final ordered featured portfolio. The portfolio generator
 * owns recognition and diversity policy; this boundary rechecks the concrete
 * path and full-cycle rotation invariants needed by the challenge surface.
 */
export function selectUtcChallenge<Candidate extends ChallengeCandidate>(
  candidates: readonly Candidate[],
  clock: ChallengeClock,
): ChallengeSelection<Candidate> {
  let period: UtcDayPeriod;
  try {
    period = utcDayPeriod(clock);
  } catch {
    return { kind: 'fallback', reason: 'invalid-clock' };
  }
  if (candidates.length === 0) {
    return { kind: 'fallback', period, reason: 'empty-pool' };
  }

  const candidateIds = new Set<string>();
  const endpointIds = new Set<string>();
  for (const candidate of candidates) {
    if (candidateIds.has(candidate.id)) {
      return { kind: 'fallback', period, reason: 'duplicate-candidate' };
    }
    candidateIds.add(candidate.id);
    if (
      !candidate.eligible ||
      candidate.id.length === 0 ||
      candidate.source.length === 0 ||
      candidate.target.length === 0 ||
      candidate.source === candidate.target ||
      !Number.isInteger(candidate.steps) ||
      candidate.steps < 2 ||
      candidate.steps > 5
    ) {
      return { kind: 'fallback', period, reason: 'ineligible-candidate' };
    }
    if (endpointIds.has(candidate.source) || endpointIds.has(candidate.target)) {
      return { kind: 'fallback', period, reason: 'repeated-endpoint' };
    }
    endpointIds.add(candidate.source);
    endpointIds.add(candidate.target);
  }

  const selectedIndex =
    ((period.index % candidates.length) + candidates.length) % candidates.length;
  return { kind: 'selected', period, candidate: candidates[selectedIndex]! };
}

/** Returns the next distinct entry in the accepted portfolio's stable order. */
export function nextChallengeCandidate<Candidate extends ChallengeCandidate>(
  candidates: readonly Candidate[],
  currentId: string,
): Candidate | null {
  if (candidates.length < 2) {
    return null;
  }
  const currentIndex = candidates.findIndex((candidate) => candidate.id === currentId);
  if (currentIndex < 0) {
    return null;
  }
  return candidates[(currentIndex + 1) % candidates.length] ?? null;
}

/**
 * Time-relative language is allowed only when the selected and current UTC
 * days are explicitly proven equal. Static pages intentionally omit the
 * current day and therefore remain truthful even when a deployment ages.
 */
export function challengeCopy(options: {
  snapshotDate: string;
  selectedPeriod?: UtcDayPeriod;
  currentPeriod?: UtcDayPeriod;
}): ChallengeCopy {
  if (
    options.selectedPeriod !== undefined &&
    options.currentPeriod !== undefined &&
    options.selectedPeriod.key === options.currentPeriod.key
  ) {
    return {
      mode: 'current-day',
      navigationLabel: 'Daily connection',
      kicker: 'Daily connection',
      callToAction: "Reveal today's connection",
      periodLabel: `${options.selectedPeriod.date} UTC`,
      titleSuffix: 'daily connection',
    };
  }
  return {
    mode: 'snapshot-fallback',
    navigationLabel: 'Featured connection',
    kicker: 'Featured connection',
    callToAction: 'Reveal the featured connection',
    periodLabel: `Data snapshot ${options.snapshotDate}`,
    titleSuffix: 'featured connection',
  };
}

/**
 * Creates one deterministic, single-answer bridge prompt. Distractors are
 * accepted first-class people from other portfolio paths, never claims about
 * who is or is not related.
 */
export function createBridgePrompt(options: {
  datasetVersion: string;
  candidate: BridgeCandidate;
  portfolio: readonly BridgeCandidate[];
  period: UtcDayPeriod;
  choiceCount?: number;
}): BridgePrompt | null {
  const choiceCount = Math.max(3, Math.min(5, options.choiceCount ?? DEFAULT_BRIDGE_CHOICE_COUNT));
  const selectedPathIds = new Set(options.candidate.people.map(({ id }) => id));
  const answers = options.candidate.people
    .slice(1, -1)
    .filter(
      (person, index, people) =>
        person.eligible &&
        person.id.length > 0 &&
        person.name.length > 0 &&
        people.findIndex(({ id }) => id === person.id) === index,
    );
  if (answers.length === 0) {
    return null;
  }
  const seed = `${options.datasetVersion}:${options.candidate.id}:${options.period.key}`;
  const answer = deterministicOrder(answers, `${seed}:answer`, ({ id }) => id)[0]!;
  const distractorsById = new Map<string, BridgePerson>();
  for (const candidate of options.portfolio) {
    for (const person of candidate.people) {
      if (
        person.eligible &&
        person.id.length > 0 &&
        person.name.length > 0 &&
        !selectedPathIds.has(person.id)
      ) {
        distractorsById.set(person.id, person);
      }
    }
  }
  const distractors = deterministicOrder(
    [...distractorsById.values()],
    `${seed}:distractors`,
    ({ id }) => id,
  ).slice(0, choiceCount - 1);
  if (distractors.length !== choiceCount - 1) {
    return null;
  }
  return {
    answerId: answer.id,
    choices: deterministicOrder(
      [answer, ...distractors].map(({ id, name }) => ({ id, name })),
      `${seed}:choices`,
      ({ id }) => id,
    ),
  };
}

function normalizedDomains(person: CollectionPerson): string[] {
  return [
    ...new Set(
      person.domains.map((domain) => domain.trim().toLocaleLowerCase('en-US')).filter(Boolean),
    ),
  ].sort((left, right) => left.localeCompare(right));
}

function spansDomains(candidate: CollectionCandidate): boolean {
  const sourceDomains = normalizedDomains(candidate.sourcePerson);
  const targetDomains = new Set(normalizedDomains(candidate.targetPerson));
  return (
    sourceDomains.length > 0 &&
    targetDomains.size > 0 &&
    !sourceDomains.some((domain) => targetDomains.has(domain))
  );
}

function spansEras(candidate: CollectionCandidate): boolean {
  return (
    candidate.sourcePerson.birthYear !== undefined &&
    candidate.targetPerson.birthYear !== undefined &&
    Math.abs(candidate.sourcePerson.birthYear - candidate.targetPerson.birthYear) >= CROSS_ERA_YEARS
  );
}

/**
 * Creates bounded, explicitly described views of the accepted featured set.
 * If fewer than three criteria are supported, it returns one honest generic
 * portfolio view instead of implying unsupported factual filters.
 */
export function createUnexpectedConnectionCollections(
  candidates: readonly CollectionCandidate[],
  maximumPerCollection = DEFAULT_COLLECTION_LIMIT,
): UnexpectedConnectionCollection[] {
  const limit = Math.max(1, Math.min(8, maximumPerCollection));
  const eligible = candidates.filter(({ eligible }) => eligible);
  const definitions = [
    {
      id: 'across-domains' as const,
      title: 'Across domains',
      description: 'Featured paths whose endpoints have different retained domain tags.',
      matches: spansDomains,
    },
    {
      id: 'across-eras' as const,
      title: 'Across eras',
      description: `Featured paths whose endpoint birth years are at least ${CROSS_ERA_YEARS} years apart.`,
      matches: spansEras,
    },
    {
      id: 'quick-surprises' as const,
      title: 'Quick surprises',
      description: 'Featured connections resolved in two or three accepted steps.',
      matches: (candidate: CollectionCandidate) => candidate.steps <= 3,
    },
    {
      id: 'deeper-paths' as const,
      title: 'Deeper paths',
      description: 'Featured connections resolved in four or five accepted steps.',
      matches: (candidate: CollectionCandidate) => candidate.steps >= 4,
    },
  ];
  const collections = definitions
    .map((definition) => ({
      id: definition.id,
      title: definition.title,
      description: definition.description,
      traceIds: eligible
        .filter(definition.matches)
        .slice(0, limit)
        .map(({ id }) => id),
    }))
    .filter(({ traceIds }) => traceIds.length > 0);
  if (collections.length >= 3) {
    return collections;
  }
  return [
    {
      id: 'accepted-featured',
      title: 'Accepted featured connections',
      description: 'A bounded view of the accepted featured connection portfolio.',
      traceIds: eligible.slice(0, limit).map(({ id }) => id),
    },
  ];
}
